// ============================================================
// ExpressValidation.jsx
//
// A branded, responsive course content page for the
// "Request Validation" lesson.
//
// Author: ProDev Linux Series (Security Branch)
// Features: Comparison of express-validator vs Joi,
//           middleware integration, and Dashboard navigation.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import RuleIcon              from "@mui/icons-material/Rule";
import VerifiedIcon          from "@mui/icons-material/Verified";
import BugReportIcon          from "@mui/icons-material/BugReport";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import SecurityIcon           from "@mui/icons-material/Security";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressValidation() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Security · Module 12"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Request <span>Validation</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Data validation is your first line of defense. By filtering incoming payloads before they hit your database, you prevent malformed data, injection attacks, and application crashes.
                    </Typography>
                </Box>

                {/* 1. EXPRESS-VALIDATOR */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<VerifiedIcon />} label="1. Using express-validator" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        A middleware-based library that lets you chain validation rules directly in your route definitions.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const { body, validationResult } = require('express-validator');", comment: "npm install express-validator" },
                        { cmd: "", comment: "" },
                        { cmd: "app.post('/signup',", comment: "" },
                        { cmd: "  body('email').isEmail().withMessage('Invalid Email'),", comment: "Inline validation" },
                        { cmd: "  body('password').isLength({ min: 6 }),", comment: "" },
                        { cmd: "  (req, res) => {", comment: "" },
                        { cmd: "    const errors = validationResult(req);", comment: "Check results" },
                        { cmd: "    if (!errors.isEmpty()) return res.status(400).json({ errors: errors.array() });", comment: "" },
                        { cmd: "    res.send('Valid signup!');", comment: "" },
                        { cmd: "  }", comment: "" },
                        { cmd: ");", comment: "" },
                    ]} />
                </Box>

                {/* 2. JOI SCHEMA VALIDATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<RuleIcon />} label="2. Declarative Schema with Joi" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Joi allows you to define a schema object. It is unopinionated and can be used anywhere in your logic, not just as middleware.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const Joi = require('joi');", comment: "npm install joi" },
                        { cmd: "", comment: "" },
                        { cmd: "const schema = Joi.object({", comment: "Define blueprint" },
                        { cmd: "  name: Joi.string().min(3).required(),", comment: "" },
                        { cmd: "  email: Joi.string().email().required()", comment: "" },
                        { cmd: "});", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "app.post('/register', (req, res) => {", comment: "" },
                        { cmd: "  const { error } = schema.validate(req.body);", comment: "Validate payload" },
                        { cmd: "  if (error) return res.status(400).json({ error: error.details[0].message });", comment: "" },
                        { cmd: "  res.send('Success!');", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 3. COMPARISON */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="3. Tool Comparison" />
                    <Grid container spacing={2}>
                        {[
                            { tool: "express-validator", type: "Middleware-Centric", pro: "Tightly coupled with Express logic. Great for simple route-level checks." },
                            { tool: "Joi", type: "Schema-Centric", pro: "Highly descriptive and reusable. Excellent for complex object structures and versioning." },
                        ].map((item) => (
                            <Grid item xs={12} md={6} key={item.tool}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14 }}>{item.tool}</Typography>
                                    <Typography sx={{ color: BRAND.white, fontSize: 12, mb: 1 }}>{item.type}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.pro}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* MODULE OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Learning Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Implement basic data sanitization and validation in Express",
                            "Utilize express-validator for middleware-level enforcement",
                            "Design declarative data blueprints using Joi schemas",
                            "Handle validation errors and return structured 400 responses",
                            "Understand when to choose schema-based vs inline validation",
                            "Apply best practices to maintain secure and clean API inputs",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Never trust the client! Even if your React frontend has validation, a user can bypass it using tools like Postman or cURL. Always perform <strong>Server-Side Validation</strong> to protect your data integrity.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
